import { connect } from 'react-redux';
import { go, goBack, push } from 'redux-first-history';
import { parsePhoneNumber } from 'libphonenumber-js';
import currencyFormatter from 'currency-formatter';
import { formatDate, formatYMDStringToTimestamp } from '@common/paymentsUtils/dateUtils';
import OnboardingConfirmationPage from './component';
import { ordinalFormat } from '@common/utils/stringUtils';
import { LANDLORD_STATUS } from '@rental-manager/constants/OnboardingConstants';
import { logError } from '@zg-rentals/log-error';

import {
  editAccountInfoPage,
  editDueDatePage,
  editInviteTenantPage,
  editMoveInCostsPage,
  editPaymentTermPage,
  editRentAmountPage,
  editTaxVerificationPage,
} from '@rental-manager/actions/OnboardingActions';
import { getFeaturePermissions } from '@rental-manager/actions/requests';
import {
  LandlordPhoneVerificationWebApiHandler_isVerified,
  PhoneVerificationSource,
} from '@zg-rentals/ts-rent-payment-client';
import { getPaymentErrorInfo } from '../../../../utils/getPaymentErrorInfo';
import { cleanPhoneNumber } from '@common/utils/numbersUtils';
import { closeNotification } from '@common/actions/NotificationActions';
import { trackValidationError } from '@common/actions/TrackingActions';
import { getAccountLast4 } from '@common/paymentsUtils/accountInfo';
import HTTP_STATUS from '@common/constants/HttpConstants';
import paths from '@rental-manager/pathsManifest';
import { generateMoveInCostPayload, getMoveInCost } from '@common/paymentsUtils/moveInCostsUtil';
import type { IdentityVerificationInfo, RentTerms, TenantInfo } from '@zg-rentals/ts-rent-payment-client';

const getAccountInfoDetails = ({ landlordStatus, accountInfo, accountInfoFromBackend }) => {
  const accountLast4 = getAccountLast4({ landlordStatus, accountInfo, accountInfoFromBackend });
  return accountLast4;
};

const getBaseMonthlyAmount = ({ monthlyRent }) => [currencyFormatter.format(monthlyRent / 100, { code: 'USD' })];

const getMonthlyPaymentDate = ({ paymentDayOfTheMonth }) => {
  if (paymentDayOfTheMonth === 31) {
    return ['Last day of the month'];
  }
  return [`${ordinalFormat(paymentDayOfTheMonth)} of the month`];
};

const getPaymentTerm = ({ firstDueDate, monthToMonth, paymentEndDate }) => [
  { label: 'First full rent payment', value: formatDate(firstDueDate, 'MMMM d, yyyy') },
  monthToMonth
    ? 'No end date/Month-to-month'
    : { label: 'Payment period ends', value: formatDate(paymentEndDate, 'MMMM d, yyyy') },
];

const getTenant = ({ tenantFirstName, tenantLastName, emailAddress }) => [
  `${tenantFirstName} ${tenantLastName}`,
  emailAddress,
];

const mapStateToProps = (state, { propertyId, listing, landlordStatus }) => {
  const {
    acceptTerms,
    paymentSettings,
    accountInfo,
    identityVerificationInfo,
    tenantInvitationInfo,
    moveInCostsSettings,
    identityCheckResponse,
  } = state.payments.landlord.onboarding[propertyId];
  const {
    accountInfo: accountInfoFromBackend,
    currentTerm,
    nextTerm,
    selectedLease,
    listingDetails,
  } = state.payments.landlord.property;
  const { paymentsEncodedLeaseId } = selectedLease || {};
  const rentTermsKey = nextTerm?.rentTermsKey || currentTerm.rentTermsKey;
  const baseMonthlyAmount = getBaseMonthlyAmount(paymentSettings);
  const monthlyPaymentDate = getMonthlyPaymentDate(paymentSettings);
  const paymentTerm = getPaymentTerm(paymentSettings);
  const moveInCosts = getMoveInCost(moveInCostsSettings);
  const depositAccount = [getAccountInfoDetails({ landlordStatus, accountInfo, accountInfoFromBackend })];
  const primaryTenant = getTenant(tenantInvitationInfo);
  const listingName = listing.name;
  const hasLandlordOnboarded =
    landlordStatus !== LANDLORD_STATUS.NOT_STARTED && landlordStatus !== LANDLORD_STATUS.EXTERNAL_ACCOUNT_SET;

  return {
    baseMonthlyAmount,
    monthlyPaymentDate,
    paymentTerm,
    moveInCosts,
    depositAccount,
    primaryTenant,
    listingName,
    hasLandlordOnboarded,
    landlordStatus,
    onboardingInfo: {
      acceptTerms,
      paymentSettings,
      accountInfo,
      identityVerificationInfo,
      tenantInvitationInfo,
      moveInCostsSettings,
      rentTermsKey,
      identityCheckResponse,
      paymentsEncodedLeaseId,
    },
    notification: state.payments.landlord.notification,
    identityVerificationInfo,
    tenantInvitationInfo,
    moveInCostsSettings,
    rentTermsKey,
    identityCheckResponse,
    user: state.user,
    currentProperty: state.payments.landlord.property.listingDetails,
    listingDetailsContactPhone: listingDetails.contactPhone,
    propertyId,
  };
};

export const mapDispatchToProps = (dispatch, ownProps) => {
  const { baseUrl, propertyId, landlordStatus, location } = ownProps;
  return {
    getVerificationPayload: ({ identityVerificationInfo, paymentSettings, tenantInvitationInfo }) => {
      dispatch(closeNotification());

      try {
        // Check if landlord identity verification is needed
        const needToSubmitId =
          landlordStatus === LANDLORD_STATUS.NOT_STARTED ||
          landlordStatus === LANDLORD_STATUS.ONBOARDING_STARTED ||
          landlordStatus === LANDLORD_STATUS.EXTERNAL_ACCOUNT_SET;

        if (!needToSubmitId) {
          return null;
        }

        // Parse phone number
        let formattedPhoneNumber;
        try {
          formattedPhoneNumber = parsePhoneNumber(identityVerificationInfo.phoneNumber, 'US').number;
        } catch {
          logError({
            error: new Error(
              `Unable to parse phone number: ${identityVerificationInfo.phoneNumber} for property ${propertyId}`,
            ),
          });
        }

        const businessStructure =
          identityVerificationInfo.selectedTaxVerificationType === 'single_member_llc' ||
          identityVerificationInfo.selectedTaxVerificationType === 'multi_member_llc'
            ? identityVerificationInfo.selectedTaxVerificationType
            : '';

        const businessType =
          identityVerificationInfo.selectedTaxVerificationType === 'individual' ? 'individual' : 'company';

        const ssn = String(identityVerificationInfo.ssn ?? '');
        const identityPayload: IdentityVerificationInfo = {
          address: String(identityVerificationInfo.address ?? ''),
          businessType,
          city: String(identityVerificationInfo.city ?? ''),
          companyAddress: String(identityVerificationInfo.companyAddress ?? ''),
          companyCity: String(identityVerificationInfo.companyCity ?? ''),
          companyDbaName: String(identityVerificationInfo.companyDbaName ?? ''),
          companyName: String(identityVerificationInfo.companyName ?? ''),
          companyState: String(identityVerificationInfo.companyState ?? ''),
          companyStructure: businessStructure,
          companyTaxId: String(identityVerificationInfo.companyTaxId ?? ''),
          companyZip: String(identityVerificationInfo.companyZip ?? ''),
          dobDay: parseInt(String(identityVerificationInfo.dobDay), 10) || 0,
          dobMonth: parseInt(String(identityVerificationInfo.dobMonth), 10) || 0,
          dobYear: parseInt(String(identityVerificationInfo.dobYear), 10) || 0,
          firstName: String(identityVerificationInfo.firstName ?? ''),
          lastName: String(identityVerificationInfo.lastName ?? ''),
          phoneNumber: formattedPhoneNumber ?? '',
          ssn,
          ssn4: ssn.slice(-4),
          state: String(identityVerificationInfo.state ?? ''),
          zip: String(identityVerificationInfo.zip ?? ''),
        };

        const rentTermsPayload: RentTerms = {
          amount: parseInt(String(paymentSettings?.monthlyRent), 10) || 0,
          dueLastDayOfMonth: paymentSettings?.paymentDayOfTheMonth === 31,
          firstDueDate: paymentSettings?.firstDueDate
            ? formatYMDStringToTimestamp(formatDate(paymentSettings.firstDueDate, 'yyyy-MM-dd 00:00'))
            : 0,
          monthToMonth: Boolean(paymentSettings?.monthToMonth),
          numberOfPayments: Number(paymentSettings?.numberOfPayments) || 0,
          propertyId,
        };

        const tenantInfoPayload: TenantInfo = {
          email: String(tenantInvitationInfo?.emailAddress ?? ''),
          firstName: String(tenantInvitationInfo?.tenantFirstName ?? ''),
          lastName: String(tenantInvitationInfo?.tenantLastName ?? ''),
        };

        return {
          propertyId,
          identityVerificationInfo: identityPayload,
          rentTerms: rentTermsPayload,
          tenantInfo: tenantInfoPayload,
        };
      } catch (err) {
        logError({ error: err, context: 'getVerificationPayload' });
        throw err;
      }
    },
    generateMoveInCostPayload,
    editMoveInCosts: () =>
      Promise.resolve()
        .then(() => dispatch(closeNotification()))
        .then(() => dispatch(editMoveInCostsPage({ propertyId })))
        .then(() => dispatch(getFeaturePermissions({ propertyId })))
        .then(() =>
          dispatch(
            push({
              pathname: baseUrl + paths.onboarding.moveInCostsPage,
              state: { fromWhere: 'onboardingReview', editTitle: 'Move-in costs' },
            }),
          ),
        ),
    editRentAmount: () =>
      Promise.resolve()
        .then(() => dispatch(closeNotification()))
        .then(() => dispatch(editRentAmountPage({ propertyId })))
        .then(() =>
          dispatch(
            push({
              pathname: baseUrl + paths.onboarding.rentAmountPage,
              state: { fromWhere: 'onboardingReview', editTitle: 'Monthly rent' },
            }),
          ),
        ),
    editDueDate: () =>
      Promise.resolve()
        .then(() => dispatch(closeNotification()))
        .then(() => dispatch(editDueDatePage({ propertyId })))
        .then(() =>
          dispatch(
            push({
              pathname: baseUrl + paths.onboarding.dueDatePage,
              state: { fromWhere: 'onboardingReview', editTitle: 'Monthly payment date' },
            }),
          ),
        ),
    editPaymentTerm: () =>
      Promise.resolve()
        .then(() => dispatch(closeNotification()))
        .then(() => dispatch(editPaymentTermPage({ propertyId })))
        .then(() =>
          dispatch(
            push({
              pathname: baseUrl + paths.onboarding.termLengthPage,
              state: { fromWhere: 'onboardingReview', editTitle: 'Payment period' },
            }),
          ),
        ),

    editAccountInfo: () =>
      Promise.resolve()
        .then(() => dispatch(closeNotification()))
        .then(() => dispatch(editAccountInfoPage({ propertyId })))
        .then(() => {
          return dispatch(
            push({
              pathname: baseUrl + paths.onboarding.accountInfoPage,
              state: { fromWhere: 'onboardingReview', editTitle: 'Deposit account' },
            }),
          );
        }),
    goToPhoneVerificationPage: () => {
      dispatch(closeNotification());
      dispatch(
        push({
          pathname: baseUrl + paths.onboarding.phoneVerificationPage,
          state: { fromWhere: paths.onboarding.confirmationPage },
        }),
      );
    },
    editIdentityVerification: () =>
      Promise.resolve()
        .then(() => dispatch(closeNotification()))
        .then(() => dispatch(editTaxVerificationPage({ propertyId })))
        .then(() =>
          dispatch(
            push({
              pathname: baseUrl + paths.onboarding.taxVerificationPage,
              state: { fromWhere: 'onboardingReview', editTitle: 'Tax information' },
            }),
          ),
        ),
    editTenantInvitationInfo: () =>
      Promise.resolve()
        .then(() => dispatch(closeNotification()))
        .then(() => dispatch(editInviteTenantPage({ propertyId })))
        .then(() =>
          dispatch(
            push({
              pathname: baseUrl + paths.onboarding.inviteTenantPage,
              state: { fromWhere: 'onboardingReview', editTitle: 'Primary tenant' },
            }),
          ),
        ),
    onBack: () =>
      Promise.resolve()
        .then(() => dispatch(closeNotification()))
        .then(() => {
          const fromPhoneVerification = location?.state?.fromWhere === paths.onboarding.phoneVerificationPage;
          return dispatch(fromPhoneVerification ? go(-2) : goBack());
        }),
    onClose: () =>
      Promise.resolve()
        .then(() => dispatch(closeNotification()))
        .then(() => dispatch(push(baseUrl + paths.onboarding.exitPaymentSetup))),
    trackValidationError: (data) => dispatch(trackValidationError(data)),
    checkIsPhoneVerificationNeeded: async ({
      phoneNumberFromOnboarding,
      listingDetailsContactPhone,
    }): Promise<boolean> => {
      try {
        const response = await LandlordPhoneVerificationWebApiHandler_isVerified({
          phone: cleanPhoneNumber(phoneNumberFromOnboarding || listingDetailsContactPhone),
          verificationSource: PhoneVerificationSource.OTP_RENT_PAYMENT,
        });
        return !response.isSessionValid || !response.isVerified;
      } catch (e: unknown) {
        const { httpStatus, errorCode } = getPaymentErrorInfo(e);
        const isPhoneInvalid =
          errorCode === 'phone-invalid' &&
          (httpStatus === HTTP_STATUS.SC_BAD_REQUEST || httpStatus === HTTP_STATUS.SC_FORBIDDEN);
        if (isPhoneInvalid) {
          return true;
        }
        throw e;
      }
    },
  };
};

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  getVerificationPayload: () =>
    dispatchProps.getVerificationPayload({
      identityVerificationInfo: stateProps.onboardingInfo.identityVerificationInfo,
      paymentSettings: stateProps.onboardingInfo.paymentSettings,
      tenantInvitationInfo: stateProps.tenantInvitationInfo,
    }),
  checkIsPhoneVerificationNeeded: () =>
    dispatchProps.checkIsPhoneVerificationNeeded({
      phoneNumberFromOnboarding: stateProps.identityVerificationInfo.phoneNumber,
      listingDetailsContactPhone: stateProps.listingDetailsContactPhone,
    }),
});

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(OnboardingConfirmationPage);
